import React, { useState, useEffect } from "react";
import {
  Lock,
  GitBranch,
  History,
  CheckCircle2,
  Clock,
  FileText,
  PenLine,
  Download,
  UserCheck,
  ShieldCheck,
} from "lucide-react";
import {
  getProductLcaVersions,
  getProductLcaVersionsCurrent,
  submitProductLcaVersion,
  createProductLcaRevision,
  getProductLcaVerificationPackage,
  patchProductLockStatus,
  getProductEpdApproval,
  extractProductFromResponse,
} from "../../../../../apis/Product/ProductEPDApi";
import Swal from "sweetalert2";

// ── Types ─────────────────────────────────────────────────────────────────────

export const LCA_STATUS = {
  DRAFT: "draft",
  SUBMITTED: "submitted",
  DRAFT_REVISION: "draft_revision",
  VERIFIED_LOCKED: "verified_locked",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const REVIEWERS = [
  "Anna Jensen",
  "Mads Christensen",
  "Sofia Larsen",
  "Henrik Møller",
  "Camilla Berg",
];

function now() {
  return new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBackendDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function mapVersionFromBackend(v) {
  if (!v) return null;
  const submittedBy = v.submitted_by ?? v.submitted_by_id ?? "—";
  const reviewerApproval = v.reviewer_approval && typeof v.reviewer_approval === "object"
    ? v.reviewer_approval
    : v.reviewer_approval
    ? { name: v.reviewer_approval, timestamp: v.reviewer_approved_at ?? "—" }
    : null;
  const signOffUser = v.sign_off_user ?? v.sign_off_user_id ?? null;
  return {
    versionId: String(v.id ?? v.version_id ?? ""),
    versionNumber: v.version_number ?? "v1.0",
    status: v.status ?? "draft",
    timestamp: formatBackendDate(v.submitted_at ?? v.updated_at),
    user: typeof submittedBy === "object" ? (submittedBy.name ?? submittedBy.email ?? "—") : submittedBy,
    reviewerApproval: reviewerApproval ?? undefined,
    signOff: signOffUser != null ? { user: typeof signOffUser === "object" ? (signOffUser.name ?? signOffUser.email ?? "—") : signOffUser, timestamp: formatBackendDate(v.sign_off_at) } : undefined,
    basedOn: v.based_on_version_number ?? v.based_on ?? undefined,
  };
}

function incrementMinorVersion(version) {
  const match = version.match(/^v(\d+)\.(\d+)$/);
  if (!match) return version;
  return `v${match[1]}.${parseInt(match[2], 10) + 1}`;
}

async function downloadVerificationPackage(version) {
  const dataset = {
    versionId: version.versionId,
    versionNumber: version.versionNumber,
    status: version.status,
    standard: "EN 15804 +A2",
    submittedAt: version.timestamp,
    submittedBy: version.user,
    reviewerApproval: version.reviewerApproval ?? null,
    signOff: version.signOff ?? null,
    modules: {
      A1_A3: { gwp: 312.4, unit: "kg CO₂ eq." },
      A4: { gwp: 18.2, unit: "kg CO₂ eq." },
      B: { gwp: 0, unit: "kg CO₂ eq." },
      C: { gwp: 42.7, unit: "kg CO₂ eq." },
      D: { gwp: -28.1, unit: "kg CO₂ eq." },
    },
  };

  const pdfText = `
LCA REPORT - VELUX GDL Cabrio® Balcony Window
Version: ${version.versionNumber}
Standard: EN 15804 +A2
Status: ${version.status}
Submitted: ${version.timestamp}
Submitted by: ${version.user}
Reviewer: ${version.reviewerApproval?.name ?? "N/A"}
Signed off: ${version.signOff ? "Yes, by " + version.signOff.user : "No"}

MODULE RESULTS
--------------
A1-A3 (Production):  312.4 kg CO2 eq.
A4 (Transport):       18.2 kg CO2 eq.
B (Use phase):         0.0 kg CO2 eq.
C (End of life):      42.7 kg CO2 eq.
D (Beyond boundary): -28.1 kg CO2 eq.

This report is generated automatically and is read-only.
  `.trim();

  const jsonBlob = new Blob([JSON.stringify(dataset, null, 2)], {
    type: "application/json",
  });
  const jsonUrl = URL.createObjectURL(jsonBlob);
  const jsonLink = document.createElement("a");
  jsonLink.href = jsonUrl;
  jsonLink.download = `LCA_dataset_${version.versionNumber}.json`;
  jsonLink.click();
  URL.revokeObjectURL(jsonUrl);

  const pdfBlob = new Blob([pdfText], { type: "application/pdf" });
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const pdfLink = document.createElement("a");
  pdfLink.href = pdfUrl;
  pdfLink.download = `LCA_Report_${version.versionNumber}.pdf`;
  pdfLink.click();
  URL.revokeObjectURL(pdfUrl);
}

// ── Status badge ──────────────────────────────────────────────────────────────

const statusMeta = {
  draft: {
    label: "Draft",
    color: "text-amber-300 bg-amber-400/15 border-amber-400/30",
    icon: Clock,
  },
  submitted: {
    label: "Submitted – Locked",
    color: "text-emerald-300 bg-emerald-400/15 border-emerald-400/30",
    icon: CheckCircle2,
  },
  draft_revision: {
    label: "Draft (Revision)",
    color: "text-sky-300 bg-sky-400/15 border-sky-400/30",
    icon: GitBranch,
  },
  verified_locked: {
    label: "Verified – Locked",
    color: "text-violet-300 bg-violet-400/15 border-violet-400/30",
    icon: ShieldCheck,
  },
};

function StatusBadge({ status }) {
  const meta = statusMeta[status] || statusMeta.draft;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.color}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {meta.label}
    </span>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const defaultVersionStub = {
  versionId: "lca-stub",
  versionNumber: "v1.0",
  status: "draft",
  timestamp: "—",
  user: "—",
};

const EPDVerificationPanel = ({
  productId,
  businessId,
  onLockChange,
  initialVersions,
  initialStatus,
  lcaLockStatus = "unlocked",
  lockRefreshTrigger = 0,
}) => {
  const [versions, setVersions] = useState(
    initialVersions && initialVersions.length > 0 ? initialVersions : [defaultVersionStub]
  );
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [epdLockLoading, setEpdLockLoading] = useState(false);
  const [calcEndLoading, setCalcEndLoading] = useState(false);
  const [epdApproval, setEpdApproval] = useState(null);

  const loadVersions = () => {
    if (!productId || !businessId) {
      setVersionsLoading(false);
      return;
    }
    setVersionsLoading(true);
    getProductLcaVersionsCurrent({
      productId,
      businessId,
      onSuccess: (res) => {
        const data = res?.data ?? res;
        const currentMapped = mapVersionFromBackend(data);
        if (currentMapped) {
          getProductLcaVersions({
            productId,
            businessId,
            onSuccess: (listRes) => {
              const list = listRes?.data ?? listRes ?? [];
              const mapped = Array.isArray(list)
                ? list.map(mapVersionFromBackend).filter(Boolean)
                : [];
              // Always treat /current as the active version (last in list for UI)
              const others = mapped.filter(
                (v) => v.versionId !== currentMapped.versionId
              );
              setVersions([...others, currentMapped]);
              setVersionsLoading(false);
            },
            onError: () => {
              setVersions([currentMapped]);
              setVersionsLoading(false);
            },
          });
        } else {
          setVersionsLoading(false);
        }
      },
      onError: () => {
        setVersionsLoading(false);
      },
    });
  };

  useEffect(() => {
    loadVersions();
  }, [productId, businessId, lockRefreshTrigger]);

  const loadEpdApproval = (onDone) => {
    if (!productId || !businessId) {
      onDone?.(null);
      return;
    }
    getProductEpdApproval({
      productId,
      businessId,
      onSuccess: (res) => {
        const raw = res?.data ?? res;
        setEpdApproval(raw);
        onDone?.(raw);
      },
      onError: () => onDone?.(null),
    });
  };

  useEffect(() => {
    loadEpdApproval();
  }, [productId, businessId, lockRefreshTrigger]);

  const current = versions[versions.length - 1] ?? defaultVersionStub;

  const [checkedDataCompleteness, setCheckedDataCompleteness] = useState(false);
  const [checkedMethodology, setCheckedMethodology] = useState(false);
  const [checkedInternalApproval, setCheckedInternalApproval] = useState(false);

  const [reviewerApproval, setReviewerApproval] = useState(null);

  const handleReviewerSelect = (name) => {
    setReviewerApproval({ name, timestamp: now() });
  };

  const [signOff, setSignOff] = useState(null);
  const [signOffModalOpen, setSignOffModalOpen] = useState(false);

  const handleConfirmSignOff = () => {
    setSignOff({ user: "Current User", timestamp: now() });
    setSignOffModalOpen(false);
  };

  const [historyOpen, setHistoryOpen] = useState(false);

  const allChecked =
    checkedDataCompleteness && checkedMethodology && checkedInternalApproval;
  const canSubmit = allChecked && reviewerApproval !== null && signOff !== null;

  const handleSubmit = () => {
    if (!productId || !businessId) {
      const submittedVersion = {
        ...current,
        status: "submitted",
        timestamp: now(),
        user: "Current User",
        reviewerApproval: reviewerApproval ?? undefined,
        signOff: signOff ?? undefined,
      };
      setVersions(versions.slice(0, -1).concat(submittedVersion));
      onLockChange?.(null);
      return;
    }
    setSubmitLoading(true);
    submitProductLcaVersion({
      productId,
      businessId,
      data: {
        reviewer_approval: reviewerApproval
          ? {
              name: reviewerApproval.name,
              timestamp: reviewerApproval.timestamp,
            }
          : null,
        reviewer_approved_at: reviewerApproval?.timestamp ?? null,
        sign_off_user: signOff?.user ?? null,
        sign_off_at: signOff?.timestamp ?? null,
      },
      onSuccess: () => {
        loadVersions();
        setSubmitLoading(false);
        onLockChange?.(null);
      },
      onError: () => setSubmitLoading(false),
    });
  };

  const handleCreateRevision = () => {
    if (!productId || !businessId) {
      const newVersion = {
        versionId: `lca-${Date.now()}`,
        versionNumber: incrementMinorVersion(current.versionNumber),
        status: "draft_revision",
        timestamp: now(),
        user: "Current User",
        basedOn: current.versionNumber,
      };
      setVersions([...versions, newVersion]);
      setCheckedDataCompleteness(false);
      setCheckedMethodology(false);
      setCheckedInternalApproval(false);
      setReviewerApproval(null);
      setSignOff(null);
      onLockChange?.(null);
      return;
    }
    setRevisionLoading(true);
    createProductLcaRevision({
      productId,
      businessId,
      onSuccess: () => {
        loadVersions();
        setCheckedDataCompleteness(false);
        setCheckedMethodology(false);
        setCheckedInternalApproval(false);
        setReviewerApproval(null);
        setSignOff(null);
        setRevisionLoading(false);
        onLockChange?.(null);
      },
      onError: (err) => {
        setRevisionLoading(false);
        Swal.fire({
          icon: "error",
          title: "Could not create revision",
          text: err?.response?.data?.message || "Create revision failed. Try again.",
        });
      },
    });
  };

  const handleDownloadPackage = () => {
    if (!productId || !businessId) {
      downloadVerificationPackage(current);
      return;
    }
    getProductLcaVerificationPackage({
      productId,
      businessId,
      onSuccess: (blob) => {
        if (blob && blob instanceof Blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `LCA_Verification_${current.versionNumber}.zip`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          downloadVerificationPackage(current);
        }
      },
      onError: (err) => {
        const status = err?.response?.status;
        if (status === 501 && typeof window !== "undefined" && window.alert) {
          window.alert("Download verification package is not yet implemented on the server. Generating local stub.");
        }
        downloadVerificationPackage(current);
      },
    });
  };

  const isSubmitted = current.status === "submitted";
  const isVerified = current.status === "verified_locked";
  const isLocked = isSubmitted || isVerified;
  const isDraft =
    current.status === "draft" || current.status === "draft_revision";

  const isEpdLocked = lcaLockStatus === "epd_locked";
  const isApprovalComplete = (approval) =>
    !!approval?.declaration_number?.trim() && !!approval?.verified_by?.trim();
  const epdApprovalComplete = isApprovalComplete(epdApproval);
  // Version must be submitted (or verified) before product-level EPD lock.
  // Approval completeness is re-checked on click from the API (header saves are not in this panel's state).
  const versionReadyForEpdLock = isSubmitted || isVerified;
  const canAttemptEpdLock =
    !isEpdLocked && versionReadyForEpdLock && !epdLockLoading;
  const canMarkCalcEnded =
    lcaLockStatus === "unlocked" && !calcEndLoading && !isEpdLocked;

  const handleMarkCalcEnded = () => {
    if (!productId || !businessId) return;
    setCalcEndLoading(true);
    patchProductLockStatus({
      productId,
      businessId,
      lca_lock_status: "calc_ended",
      onSuccess: (res) => {
        setCalcEndLoading(false);
        const product = extractProductFromResponse(res);
        onLockChange?.(product ?? { lca_lock_status: "calc_ended" });
        Swal.fire({
          icon: "success",
          title: "Calculation finished",
          text: "Product marked as calculation ended.",
          timer: 2000,
          showConfirmButton: false,
        });
      },
      onError: (err) => {
        setCalcEndLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.response?.data?.message || "Could not update lock status.",
        });
      },
    });
  };

  const runEpdLockPatch = () => {
    patchProductLockStatus({
      productId,
      businessId,
      lca_lock_status: "epd_locked",
      onSuccess: (res) => {
        setEpdLockLoading(false);
        const product = extractProductFromResponse(res);
        const status = product?.lca_lock_status;

        // Backend sometimes returns 200 without applying epd_locked — treat as failure
        if (status !== "epd_locked") {
          onLockChange?.(product ?? null);
          Swal.fire({
            icon: "error",
            title: "EPD lock not applied",
            html:
              `Requested <code>epd_locked</code> but product is still <code>${status || "unknown"}</code>.` +
              `<br/><br/>This is a backend issue — the lock-status endpoint accepted the request but did not update the status or publish the DPP.` +
              (product?.dpp_is_published === false
                ? `<br/><br/><code>dpp_is_published</code> is still false.`
                : ""),
          });
          return;
        }

        onLockChange?.(product);
        Swal.fire({
          icon: "success",
          title: "EPD locked",
          text: product?.dpp_is_published
            ? "Digital Product Passport published."
            : "EPD locked. DPP publish may still be pending — refresh if the panel does not appear.",
          timer: 2500,
          showConfirmButton: false,
        });
      },
      onError: (err) => {
        setEpdLockLoading(false);
        Swal.fire({
          icon: "error",
          title: "Lock failed",
          text: err?.response?.data?.message || "Could not lock EPD and publish DPP.",
        });
      },
    });
  };

  const handleLockEpdAndPublishDpp = async () => {
    if (!versionReadyForEpdLock) {
      Swal.fire({
        icon: "info",
        title: "Submit LCA first",
        text: "Submit the LCA version for verification before locking the EPD.",
      });
      return;
    }

    setEpdLockLoading(true);

    const proceedWithLock = () => {
      if (!productId || !businessId) {
        setEpdLockLoading(false);
        onLockChange?.({
          lca_lock_status: "epd_locked",
          dpp_is_published: true,
        });
        return;
      }

      Swal.fire({
        icon: "warning",
        title: "Lock EPD & Publish DPP?",
        text: "This permanently locks the EPD and publishes the Digital Product Passport. The product data will become read-only. Continue?",
        showCancelButton: true,
        confirmButtonText: "Lock & Publish",
        confirmButtonColor: "#dc2626",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (!result.isConfirmed) {
          setEpdLockLoading(false);
          return;
        }

        // Allowed transition: unlocked → calc_ended → epd_locked
        if (lcaLockStatus === "unlocked") {
          patchProductLockStatus({
            productId,
            businessId,
            lca_lock_status: "calc_ended",
            onSuccess: () => runEpdLockPatch(),
            onError: (err) => {
              setEpdLockLoading(false);
              Swal.fire({
                icon: "error",
                title: "Lock failed",
                text:
                  err?.response?.data?.message ||
                  "Could not mark calculation finished before EPD lock.",
              });
            },
          });
        } else {
          runEpdLockPatch();
        }
      });
    };

    // Re-fetch approval so we see values just saved in the header form
    loadEpdApproval((approval) => {
      if (!isApprovalComplete(approval)) {
        setEpdLockLoading(false);
        Swal.fire({
          icon: "info",
          title: "EPD approval incomplete",
          text: "Fill Declaration number and Verified by in EPD Approval, wait a second for autosave, then try again.",
        });
        return;
      }
      proceedWithLock();
    });
  };

  return (
    <>
      <div
        className="rounded-3xl overflow-hidden shadow-lg"
        style={{ background: "hsl(221 39% 24%)" }}
      >
        <div className="p-8 space-y-5">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-white/70 flex-shrink-0" />
            <h3 className="text-sm font-semibold text-white">EPD Verification</h3>
            {versionsLoading && (
              <span className="text-[10px] text-white/50">Loading…</span>
            )}
          </div>

          <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-white/50 uppercase tracking-wide text-[10px]">
                Status
              </span>
              <StatusBadge status={current.status} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/50 uppercase tracking-wide text-[10px]">
                Version
              </span>
              <span className="text-white font-semibold">{current.versionNumber}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/50 uppercase tracking-wide text-[10px]">
                Standard
              </span>
              <span className="text-white/80">EN 15804 +A2</span>
            </div>

            {isLocked ? (
              <>
                <div className="pt-1 border-t border-white/10" />
                <div className="flex items-center justify-between">
                  <span className="text-white/50 uppercase tracking-wide text-[10px]">
                    Submitted
                  </span>
                  <span className="text-white/80">{current.timestamp}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/50 uppercase tracking-wide text-[10px]">
                    Submitted by
                  </span>
                  <span className="text-white/80">{current.user}</span>
                </div>
                <div className="flex items-center gap-1 pt-1">
                  <Lock className="h-3 w-3 text-emerald-400" />
                  <span className="text-emerald-400 text-[10px] font-medium">
                    Permanently locked
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-white/50 uppercase tracking-wide text-[10px]">
                    Last modified
                  </span>
                  <span className="text-white/80">{current.timestamp}</span>
                </div>
                {current.basedOn && (
                  <div className="flex items-center justify-between">
                    <span className="text-white/50 uppercase tracking-wide text-[10px]">
                      Based on
                    </span>
                    <span className="text-white/80">{current.basedOn}</span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <UserCheck className="h-3.5 w-3.5 text-white/50" />
              <p className="text-[10px] text-white/50 uppercase tracking-wide font-medium">
                Internal Review Approved by
              </p>
            </div>

            {isLocked ? (
              <div className="rounded-xl bg-white/5 border border-white/10 p-2.5 space-y-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-[10px] uppercase tracking-wide">
                    Reviewer
                  </span>
                  <span className="text-white/90 font-medium">
                    {current.reviewerApproval?.name ?? "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-[10px] uppercase tracking-wide">
                    Approved at
                  </span>
                  <span className="text-white/70">
                    {current.reviewerApproval?.timestamp ?? "—"}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <select
                  value={reviewerApproval?.name ?? ""}
                  onChange={(e) => handleReviewerSelect(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-xl h-9 text-xs hover:bg-white/15 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-white/30"
                >
                  <option value="">Select reviewer…</option>
                  {REVIEWERS.map((r) => (
                    <option key={r} value={r} className="text-gray-900">
                      {r}
                    </option>
                  ))}
                </select>
                {reviewerApproval && (
                  <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 className="h-2.5 w-2.5" />
                    Approved at {reviewerApproval.timestamp}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-white/50" />
              <p className="text-[10px] text-white/50 uppercase tracking-wide font-medium">
                Digital Sign-off
              </p>
            </div>

            {isLocked ? (
              <div className="rounded-xl bg-white/5 border border-white/10 p-2.5 space-y-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-[10px] uppercase tracking-wide">
                    Signed
                  </span>
                  <span
                    className={
                      current.signOff
                        ? "text-emerald-400 font-semibold"
                        : "text-white/50"
                    }
                  >
                    {current.signOff ? "Yes" : "No"}
                  </span>
                </div>
                {current.signOff && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-[10px] uppercase tracking-wide">
                        Signed by
                      </span>
                      <span className="text-white/80">{current.signOff.user}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-[10px] uppercase tracking-wide">
                        Signed at
                      </span>
                      <span className="text-white/70">
                        {current.signOff.timestamp}
                      </span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {signOff ? (
                  <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-2.5 space-y-1 text-xs">
                    <div className="flex items-center gap-1.5 text-emerald-400">
                      <CheckCircle2 className="h-3 w-3" />
                      <span className="font-semibold">
                        Signed by {signOff.user}
                      </span>
                    </div>
                    <p className="text-white/50 text-[10px]">{signOff.timestamp}</p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSignOffModalOpen(true)}
                    className="w-full h-9 text-xs font-semibold border border-white/20 text-white bg-transparent hover:bg-white/10 rounded-xl flex items-center justify-center gap-1.5"
                  >
                    <PenLine className="h-3.5 w-3.5" />
                    Digital Sign-off
                  </button>
                )}
              </>
            )}
          </div>

          {isDraft && (
            <div className="space-y-2.5">
              <p className="text-[10px] text-white/50 uppercase tracking-wide font-medium">
                Pre-submission checklist
              </p>
              {[
                {
                  id: "data",
                  label: "Confirm data completeness",
                  checked: checkedDataCompleteness,
                  onChange: setCheckedDataCompleteness,
                },
                {
                  id: "method",
                  label: "Confirm methodology compliance",
                  checked: checkedMethodology,
                  onChange: setCheckedMethodology,
                },
                {
                  id: "approval",
                  label: "Confirm internal approval",
                  checked: checkedInternalApproval,
                  onChange: setCheckedInternalApproval,
                },
              ].map((item) => (
                <div key={item.id} className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    id={item.id}
                    checked={item.checked}
                    onChange={(e) => item.onChange(e.target.checked)}
                    className="mt-0.5 border-white/30 rounded h-3.5 w-3.5 bg-white/10 checked:bg-emerald-500 checked:border-emerald-500 focus:ring-emerald-500"
                  />
                  <label
                    htmlFor={item.id}
                    className="text-xs text-white/80 leading-tight cursor-pointer"
                  >
                    {item.label}
                  </label>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            {isDraft && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitLoading}
                className="w-full h-12 text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-40 disabled:cursor-not-allowed rounded-xl"
              >
                {submitLoading ? "Submitting…" : "Submit for Verification"}
              </button>
            )}

            {isLocked && (
              <>
                <button
                  type="button"
                  onClick={handleCreateRevision}
                  disabled={revisionLoading || isEpdLocked}
                  className="w-full h-12 text-xs font-semibold border border-white/20 text-white bg-transparent hover:bg-white/10 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-40"
                >
                  <GitBranch className="h-3.5 w-3.5" />
                  {revisionLoading ? "Creating…" : "Create Revision"}
                </button>

                <button
                  type="button"
                  onClick={handleDownloadPackage}
                  className="w-full h-12 text-xs font-semibold border border-emerald-500/40 text-emerald-300 bg-transparent hover:bg-emerald-500/10 rounded-xl flex items-center justify-center gap-1.5"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download Verification Package
                </button>
              </>
            )}

            {!isEpdLocked && (
              <>
                {canMarkCalcEnded && (
                  <button
                    type="button"
                    onClick={handleMarkCalcEnded}
                    disabled={calcEndLoading}
                    className="w-full h-10 text-xs font-semibold border border-white/20 text-white/80 bg-transparent hover:bg-white/10 rounded-xl disabled:opacity-40"
                  >
                    {calcEndLoading ? "Updating…" : "Mark calculation finished"}
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleLockEpdAndPublishDpp}
                  disabled={!canAttemptEpdLock}
                  title={
                    isEpdLocked
                      ? "EPD is already locked"
                      : !versionReadyForEpdLock
                      ? "Submit LCA version for verification first"
                      : !epdApprovalComplete
                      ? "Complete Declaration number and Verified by in EPD Approval (autosaves)"
                      : "Lock EPD and publish Digital Product Passport"
                  }
                  className="w-full h-12 text-xs font-semibold bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed rounded-xl flex items-center justify-center gap-1.5"
                >
                  <Lock className="h-3.5 w-3.5" />
                  {epdLockLoading ? "Locking…" : "Lock EPD & Publish DPP"}
                </button>
                {versionReadyForEpdLock && !epdApprovalComplete && (
                  <p className="text-[10px] text-amber-300/90 text-center leading-snug">
                    Fill Declaration number + Verified by above, wait for autosave (~1s), then click Lock.
                  </p>
                )}
              </>
            )}

            {isEpdLocked && (
              <div className="flex items-center gap-1.5 justify-center py-2">
                <ShieldCheck className="h-3.5 w-3.5 text-violet-400" />
                <span className="text-violet-300 text-[10px] font-medium">
                  EPD locked — Digital Product Passport published
                </span>
              </div>
            )}

            {versions.length > 1 && (
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="w-full h-7 text-[11px] text-white/50 hover:text-white/80 hover:bg-white/5 rounded-xl flex items-center justify-center gap-1.5 bg-transparent border-0"
              >
                <History className="h-3 w-3" />
                View Revision History
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Digital Sign-off Modal */}
      {signOffModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setSignOffModalOpen(false)}
            aria-hidden="true"
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-2 mb-4">
              <PenLine className="h-5 w-5 text-emerald-600" />
              <h2 className="text-lg font-semibold">Digital Sign-off</h2>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 p-4 text-sm leading-relaxed mb-4">
              "I confirm this LCA complies with <strong>EN 15804 +A2</strong> and
              is ready for third-party verification."
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setSignOffModalOpen(false)}
                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSignOff}
                className="px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center gap-1.5"
              >
                <ShieldCheck className="h-4 w-4" />
                Confirm Sign-off
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revision History Modal */}
      {historyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setHistoryOpen(false)}
            aria-hidden="true"
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-auto p-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="h-5 w-5 text-emerald-600" />
              <h2 className="text-lg font-semibold">Revision History</h2>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-800/50">
                    <th className="text-left p-2 text-xs font-medium">Version</th>
                    <th className="text-left p-2 text-xs font-medium">Status</th>
                    <th className="text-left p-2 text-xs font-medium">Date</th>
                    <th className="text-left p-2 text-xs font-medium">User</th>
                    <th className="text-left p-2 text-xs font-medium">Reviewer</th>
                    <th className="text-left p-2 text-xs font-medium">Signed</th>
                    <th className="text-left p-2 text-xs font-medium">Based on</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.versionId} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="p-2 font-mono font-semibold">{v.versionNumber}</td>
                      <td className="p-2">
                        <StatusBadge status={v.status} />
                      </td>
                      <td className="p-2 text-gray-500 text-xs">{v.timestamp}</td>
                      <td className="p-2 text-gray-500 text-xs">{v.user}</td>
                      <td className="p-2 text-gray-500 text-xs">
                        {v.reviewerApproval?.name ?? "—"}
                      </td>
                      <td className="p-2 text-xs">
                        {v.signOff ? (
                          <span className="text-emerald-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-500">No</span>
                        )}
                      </td>
                      <td className="p-2 text-gray-500 text-xs">
                        {v.basedOn ?? "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 text-center pt-3">
              This view is read-only. Submitted versions are permanently locked.
            </p>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EPDVerificationPanel;
